<script setup>
import { ref, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useWebSiteStore } from '@/stores/web-site.store'

import { useClone, useIsUpdated } from '@/composables/object'
import { useNavigator } from '@/composables/navigator'
import { useEscapeClose } from '@/composables/keyboard'
import { useDetectDevice } from '@/composables/device'
import { useNotify } from '@/composables/notify'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import CurrencySelect from '@/components/shared/CurrencySelect.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  currencies: {
    type: Array,
    required: true,
  },
  rateLabel: {
    type: String,
    required: true,
  },
  isNew: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update', 'cancel'])

const i18n = getI18n()
const webSiteStore = useWebSiteStore()
const { isMobile } = useDetectDevice()
const { notifySuccess } = useNotify()

const form = ref(null)
const editItem = ref({})
const showDeleteDialog = ref(false)
const { isUpdated, isUpdating, setOriginal } = useIsUpdated(editItem)

useNavigator(() => emit('cancel'))
useEscapeClose(isUpdated, () => emit('cancel'))

onMounted(() => {
  editItem.value = useClone(props.modelValue)
  setOriginal(editItem.value)
})

const requiredRule = (val) => (val !== null && val !== undefined && val !== '') || i18n.t('common.required')
const positiveRateRule = (val) => Number(val) > 0 || i18n.t('error.validation.exchangeRateNotPositive')

const saveAsync = async(values, message) => {
  isUpdating.value = true
  try {
    await webSiteStore.updateCurrencyAsync(editItem.value.currency_ref, values)
    notifySuccess(message)
    emit('update', editItem.value)
  } finally {
    isUpdating.value = false
  }
}

const onSave = async() => {
  if (!await form.value.validate()) return
  const { exchange_rate, exchange_rate_date } = editItem.value
  await saveAsync(
    { exchange_rate: Number(exchange_rate), exchange_rate_date },
    props.isNew ? i18n.t('info.createdSuccessfully') : i18n.t('info.updatedSuccessfully'),
  )
}

const onDelete = () => saveAsync({ exchange_rate: null, exchange_rate_date: null }, i18n.t('info.deletedSuccessfully'))
</script>

<template>
  <q-card
    v-drag-dialog="!isMobile"
    class="dialog-card-flex"
  >
    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDelete"
    />
    <navigate-back
      :text="props.isNew ? i18n.t('company.exchangeRates.addManualRate') : i18n.t('company.exchangeRates.editManualRate')"
      @cancel="emit('cancel')"
    />
    <q-card-section class="scroll col q-pa-md" :class="{ 'dialog-card-content': !props.isNew }">
      <q-form ref="form">
        <div class="row q-col-gutter-sm">
          <currency-select
            v-model="editItem.currency_ref"
            :class="isMobile ? 'col-12' : 'col-6'"
            :options="props.currencies"
            :clearable="false"
            :readonly="!props.isNew"
            lazy-rules
            :rules="[requiredRule]"
          />
          <q-input
            v-model.number="editItem.exchange_rate"
            :class="isMobile ? 'col-12' : 'col-3'"
            :label="props.rateLabel"
            type="number"
            min="0"
            step="any"
            input-class="text-right"
            :autofocus="!props.isNew"
            lazy-rules
            :rules="[requiredRule, positiveRateRule]"
          />
          <q-input
            v-model="editItem.exchange_rate_date"
            :class="isMobile ? 'col-12' : 'col-3'"
            :label="i18n.t('company.exchangeRates.rateDate')"
            type="date"
            stack-label
            lazy-rules
            :rules="[requiredRule]"
          />
        </div>
      </q-form>
    </q-card-section>
    <q-card-actions v-if="props.isNew" align="right" class="q-pa-md">
      <q-btn
        color="primary"
        :label="i18n.t('common.confirm')"
        :loading="isUpdating"
        @click="onSave"
      />
    </q-card-actions>
    <q-card-actions v-else class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn q-mr-sm"
        color="negative"
        :label="i18n.t('common.delete')"
        @click="showDeleteDialog = true"
      />
      <q-btn
        v-if="isUpdated"
        class="large-btn"
        color="positive"
        :label="i18n.t('common.update')"
        :loading="isUpdating"
        @click="onSave"
      />
    </q-card-actions>
  </q-card>
</template>
